import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import moment, { type Moment } from 'moment';
import { groupByQuery, timeseriesQuery } from '../constants';
import { get, isNumber, orderBy } from 'lodash';
import { addMetricFilterTag, getMetricFilterMetric } from '@src/utils/table/mfUtils';
import {
  calcPercentage,
  getPctMetric,
  getSSFPctMetric,
  metricsToCallSSFPct,
} from '@src/utils/table/ssfUtils';
import { UNKNOWN_NAME } from '@src/utils/constants';
import {
  genHints,
  genNetworkRequestUrl,
  compositeNetworkRequestUrlDimension,
  rcaMetrics,
} from '@src/utils/table/rcaUtils';
import { getMetricMetaByKey } from '@src/utils/metrics';
import { MF_LABEL } from '@src/components/metrics-panel-hz/utils/metricFilterUtils';
import HintCell from '../components/HintCell';
import ValueCell from '../components/ValueCell';
import RootCell from '../components/RootCell';

export const buildTimeseriesQuery = ({ metrics, filter, interval, granularity }) => {
  const q = {
    ...cloneDeep(timeseriesQuery),
    interval,
    filter,
    metrics: [...metrics],
    granularity: granularity || 'ALL',
  };
  return q;
};

export const buildGroupByQuery = ({ metrics, dimension, filter, interval, limit = 25 }) => {
  const q = {
    ...cloneDeep(groupByQuery),
    interval,
    filter,
    metrics: [...metrics],
    groupBy: [dimension],
    sortBy: [{ metric: metrics[0], order: 'desc' }],
    limit,
  };
  return q;
};

export function formatQueryStatus({ isFetching, isError, isEmpty, refetch, queued }) {
  return {
    fetchData: refetch,
    isLoading: isFetching,
    showError: isError,
    isEmpty,
    queued,
  };
}

export const MAX_INTERVAL = 24 * 60 * 60; // 24 hours in seconds

export function validateInterval({ from, to }: { from: Moment; to: Moment }) {
  const diff = moment(to).diff(from, 'seconds');
  return diff <= MAX_INTERVAL;
}

export function formatMultiData({ data = {}, sortBy, searchText }) {
  const { metadataFilterData = {}, metricFilterData = {} }: any = data;

  // move unknown rows to bottom
  let orderedRows: any = [];
  const nanRows: unknown[] = [];
  const unknownRows: any = [];
  let totals = {};

  if (
    get(metadataFilterData, ['rows'], []).length > 0 &&
    get(metricFilterData, ['rows'], []).length > 0
  ) {
    totals = {
      name: ['Total'],
      ...metadataFilterData.totals,
      ...addMetricFilterTag(metricFilterData.totals),
      disableDrilldown: true,
    };
    for (let i = 0; i < metadataFilterData.rows.length; i++) {
      const rawData = metadataFilterData.rows[i];
      const sfData = addMetricFilterTag(get(metricFilterData, ['rows', i], {}));

      // flattened
      const row = {
        ...rawData,
        ...sfData,
      };

      /** Calculate percetage value for metric in metricsToCallSSFPct */
      for (const metric of metricsToCallSSFPct) {
        const sfTotal = totals[getMetricFilterMetric(metric)];
        const total = totals[metric];
        const ssfPctKey = getSSFPctMetric(metric);
        const pctKey = getPctMetric(metric);
        const sfValue = row[getMetricFilterMetric(metric)];
        row[ssfPctKey] = calcPercentage(sfTotal, sfValue);
        const value = row[metric];
        row[pctKey] = calcPercentage(total, value);
      }

      const name = row.name.join('');
      if (UNKNOWN_NAME.includes(name)) {
        unknownRows.push({ ...row, disableDrilldown: false });
        // Filter out all NaN, such as "NA"
        // because the presence of NA will cause _.orderBy not to work properly
      } else if (sortBy?.key && !isNumber(get(row, sortBy.key))) {
        nanRows.push(row);
      } else {
        orderedRows.push(row);
      }
    }

    if (sortBy) {
      orderedRows = orderBy(orderedRows, [sortBy.key], [sortBy.order]);
    }
  }

  let filteredRows = orderedRows.concat(nanRows, unknownRows);

  /** Calculate total percentage value */
  for (const metric of metricsToCallSSFPct) {
    const ssfPctKey = getSSFPctMetric(metric);
    const pctKey = getPctMetric(metric);
    const { sfValue, value } = filteredRows.reduce(
      (sum, row) => {
        const sfValue = row[getMetricFilterMetric(metric)];
        const value = row[metric];
        if (isNumber(sfValue)) {
          sum.sfValue += sfValue;
        }
        if (isNumber(value)) {
          sum.value += value;
        }
        return sum;
      },
      {
        sfValue: 0,
        value: 0,
      },
    );
    totals[ssfPctKey] = sfValue === 0 ? 0 : 100;
    totals[pctKey] = value === 0 ? 0 : 100;
  }
  // search
  if (searchText) {
    const regex = new RegExp(searchText, 'i'); // ignore case
    filteredRows = filteredRows.filter((row) => regex.test(row.name.join('')));
  }

  return { rows: filteredRows, totals };
}

export const addRCADimension = (_rows, rcaData) => {
  return _rows.map((item) => {
    return {
      ...item,
      network_api_calls: genNetworkRequestUrl(item),
      hints: genHints(item, rcaData),
    };
  });
};

const DIMENSION_COL_WIDTH = 120;
const METRIC_COL_WIDTH = 40;

export const formatRCAColumns = () => {
  const dimension = compositeNetworkRequestUrlDimension;
  const metrics = rcaMetrics;

  const columnConfig: any[] = [];

  // first column is the dimension value
  columnConfig.push({
    title: 'Network API Calls',
    dataKey: dimension,
    key: dimension,
    width: DIMENSION_COL_WIDTH,
    minWidth: DIMENSION_COL_WIDTH,
    frozen: 'left',
    resizable: true,
    disableDrilldown: false,
    cellRenderer: ({ cellData, column, rowData }) => (
      <RootCell cellData={cellData} column={column} rowData={rowData} />
    ),
  });

  // remaining columns are the metric values
  metrics.forEach((metric) => {
    const metricInfo = getMetricMetaByKey({ metricKey: metric });

    const mfMetricAbs = getMetricFilterMetric(metric);
    const ssfPct = getSSFPctMetric(metric);
    const mfTitle = `[${MF_LABEL}] ` + metricInfo.short;

    columnConfig.push({
      title: mfTitle,
      dataKey: mfMetricAbs,
      key: mfMetricAbs,
      metric,
      metricInfo,
      flexGrow: 1,
      align: 'right',
      width: METRIC_COL_WIDTH,
      minWidth: METRIC_COL_WIDTH,
      sortable: true,
      mfPctChecked: false, // indicator to always use unit as % when %MF is selected
      cellRenderer: ({ cellData, column }) => <ValueCell cellData={cellData} column={column} />,
      // headerRenderer: (props) => (
      //   <MetricHeader {...props} helpLookup={COLUMN_HEADER_METRIC_FILTER_HELPTEXT} />
      // ),
    });
  });

  // hints
  columnConfig.push({
    title: 'Reason',
    dataKey: 'hints',
    key: 'hints',
    flexGrow: 1,
    align: 'right',
    width: METRIC_COL_WIDTH + 30,
    minWidth: METRIC_COL_WIDTH + 30,
    cellRenderer: ({ cellData }) => <HintCell cellData={cellData} />,
  });

  return columnConfig;
};
